/* tslint:disable:max-line-length */
import * as echarts from 'echarts/lib/echarts.js';
import 'echarts/lib/chart/heatmap';
import 'echarts/lib/component/geo';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/visualMap';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/polar';
import 'echarts/lib/component/tooltip';
import { merge } from 'lodash';

import ChartService from './ChartService';
import specialAreasJson from '@/data/maps/special-areas.json';
import usMapJson from '@/data/maps/us.json';

export default class ChartGeoHeatMapService extends ChartService {

  specialAreas = { ...specialAreasJson };

  buildChartData(visualization) {
    const percentileCutoff = 0.98; // don't use the max value as max color because then only new york shows up
    const values = visualization.data.map((node) => node.count);
    const sorted = values.sort((a, b) => a - b);
    const numNodes = sorted.length;
    const upperRangeCutoffIndex = Math.min(numNodes - 1, Math.round(numNodes * percentileCutoff));
    const upperRangeCutoff = sorted[upperRangeCutoffIndex];

    const range = [Math.min(...values), upperRangeCutoff];
    const seriesData = visualization.data.map((node) => {
      node = this.transformCoordinatesInSpecialAreas(node, visualization.mapId);
      return [
        node.longitude,
        node.latitude,
        node.count,
      ];
    });

    const options = {

      geo: {
        itemStyle: {
          areaColor: '#fff',
          emphasis: {
            areaColor: '#fff',
          }
        },
        label: {
          emphasis: {
            show: false,
          }
        },
        map: visualization.mapId,
        roam: false,
        zoom: 1.1,
      },

      series: [{
        blurSize: 5,
        coordinateSystem: 'geo',
        data: seriesData,
        pointSize: 7,
        type: 'heatmap'
      }],

      visualMap: [{
        bottom: 20,
        hoverLink: false,
        inRange: {
          color: ['#60E8C5', '#60E8C5', '#276c98'], // low to high
        },
        itemWidth: 14,
        itemHeight: 400,
        left: 'center',
        max: range[1],
        min: range[0],
        orient: 'horizontal',
        text: [
          range[1].toLocaleString('default'),
          range[0].toLocaleString('default'),
        ],
        textStyle: {
          color: '#000',
          fontWeight: 'bold',
        },
        type: 'continuous',
        seriesIndex: 0,
      }],

    };

    return merge(this.defaultChartOptions, options);
  }

  render(container, chartOptions) {
    const chart = echarts.init(container);
    const mapId = chartOptions.geo.map;
    this.loadMap(mapId, () => {
      chart.setOption(chartOptions);
    });
    return chart;
  }

  loadMap(mapId, callback) {
    const mapData = usMapJson;
    const specialAreasData = this.specialAreas[mapId] || {};
    const specialAreas = Object.keys(specialAreasData)
      .reduce((result, areaName) => {
        // This code finds the `original` values in special-areas.json
        // const feature = this.getFeatureInCollectionByName(response.data, areaName);
        // const bounds = this.getGeoJsonFeatureBoundingBox(feature);
        // console.log(feature, bounds);
        result[areaName] = specialAreasData[areaName].transformed;
        return result;
      }, {});

    echarts.registerMap(mapId, mapData, specialAreas);

    callback();
  }

  transformCoordinatesInSpecialAreas(node, mapId) {
    const specialAreas = this.specialAreas[mapId];
    Object.keys(specialAreas).forEach((name) => {
      const area = specialAreas[name];
      if (this.nodeInSpecialArea(node, area)) {
        node.longitude = area.transformed.width * (Math.abs(area.original.left - node.longitude) / area.original.width) + area.transformed.left;
        node.latitude = area.transformed.height * (Math.abs(area.original.top - node.latitude) / area.original.height) + area.transformed.top;
      }
    });
    return node;
  }

  nodeInSpecialArea(node, specialArea) {
    const bounds = specialArea.original;
    return node.latitude >= bounds.top
      && node.latitude <= bounds.top + bounds.height
      && node.longitude >= bounds.left
      && node.longitude <= bounds.left + bounds.width;
  }

  getFeatureInCollectionByName(collection, name) {
    return collection.features.find((feature) => feature.properties.name === name);
  }

  getGeoJsonFeatureBoundingBox(feature) {
    const coordinates = feature.geometry.coordinates;
    const bounds = {};
    let latitude;
    let longitude;

    if (coordinates.length === 1) {
      // Polygon features
      for (let j = 0; j < coordinates[0].length; j++) { // tslint:disable-line
        longitude = coordinates[0][j][0];
        latitude = coordinates[0][j][1];
        bounds.xMin = bounds.xMin < longitude ? bounds.xMin : longitude;
        bounds.xMax = bounds.xMax > longitude ? bounds.xMax : longitude;
        bounds.yMin = bounds.yMin < latitude ? bounds.yMin : latitude;
        bounds.yMax = bounds.yMax > latitude ? bounds.yMax : latitude;
      }
    } else {
      // MultiPolygon features
      for (let j = 0; j < coordinates.length; j++) { // tslint:disable-line
        for (let k = 0; k < coordinates[j][0].length; k++) { // tslint:disable-line
          longitude = coordinates[j][0][k][0];
          latitude = coordinates[j][0][k][1];
          bounds.xMin = bounds.xMin < longitude ? bounds.xMin : longitude;
          bounds.xMax = bounds.xMax > longitude ? bounds.xMax : longitude;
          bounds.yMin = bounds.yMin < latitude ? bounds.yMin : latitude;
          bounds.yMax = bounds.yMax > latitude ? bounds.yMax : latitude;
        }
      }
    }
    bounds.top = bounds.yMin;
    bounds.left = bounds.xMin;
    bounds.width = bounds.xMax - bounds.xMin;
    bounds.height = bounds.yMax - bounds.yMin;
    return bounds;
  }

}
